/**
 * @fileoverview Filter provider component for global filter state management.
 * @module shared/providers/FilterProvider
 */

import { ReactElement, useState, ReactNode, useEffect } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import {
  FilterContext,
  FilterContextType,
  SupportedCountryDTO,
  AggregationType,
} from '@/shared/context/FilterContext'
// Use the validated Variant type and API from variantService to guarantee array shape via Zod
import { type Variant, getAllVariants } from '@/lib/variantService'
import { countryService } from '@/lib/countryService'
import { getCurrentMonthRange, inferTimeAggregation } from '@/shared/utils/dateUtils'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useTagsQuery } from '@/features/tags'

/**
 * Props for FilterProvider component.
 */
interface FilterProviderProps {
  /** Child components to receive filter context */
  children: ReactNode
}

/**
 * Provider component that manages global filter state.
 *
 * @component
 * @example
 * ```tsx
 * <FilterProvider>
 *   <App />
 * </FilterProvider>
 * ```
 */
export function FilterProvider({ children }: FilterProviderProps): ReactElement {
  // Initialize state for each filter with current month as default
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthRange()
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate)
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate)
  const [aggregation, setAggregation] = useState<AggregationType>('Monthly')
  const [countries, setCountries] = useState<string[]>([])
  const [skus, setSkus] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])
  const { activeOrgId } = useActiveOrg()

  // Available tags from API (gated on an active organization)
  const { data: tagsData, isLoading: isLoadingTags } = useTagsQuery({
    enabled: Boolean(activeOrgId),
  })
  const availableTags = tagsData ?? []

  // State for available countries and variants from API
  const [availableCountries, setAvailableCountries] = useState<SupportedCountryDTO[]>([])
  const [availableVariants, setAvailableVariants] = useState<Variant[]>([])
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(true)
  const [isLoadingVariants, setIsLoadingVariants] = useState<boolean>(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Profit basis toggle (default includes business costs)
  const [profitBasis, setProfitBasis] = useState<'OC' | 'CM2'>(() => {
    const fromUrl = (searchParams.get('profit') || '').toLowerCase()
    return fromUrl === 'cm2' ? 'CM2' : 'OC'
  })

  // Initialize dates and aggregation on mount
  useEffect(() => {
    const now = new Date()
    const normStart = new Date(defaultStartDate)
    normStart.setHours(0, 0, 0, 0)
    const normEnd = new Date(now)
    normEnd.setHours(23, 59, 59, 999)

    setStartDate(normStart)
    setEndDate(normEnd)

    const inferredAggregation = inferTimeAggregation(normStart, normEnd, aggregation)
    if (inferredAggregation !== aggregation) {
      setAggregation(inferredAggregation)
    }
    // Behaviorally this effect should run once on mount to set initial range and aggregation.
    // We intentionally keep an empty dependency array to avoid recomputing and flicker on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep URL param (?profit=oc|cm2) in sync with state — only on /dashboard routes
  // to avoid stripping URL hash fragments on auth callback and public pages.
  // Guard on activeOrgId to avoid racing with RequireOrg's redirect to /onboarding.
  useEffect(() => {
    if (!activeOrgId || !location.pathname.startsWith('/dashboard')) return
    const current = (searchParams.get('profit') || '').toLowerCase()
    const desired = profitBasis === 'OC' ? 'oc' : 'cm2'
    if (current !== desired) {
      const next = new URLSearchParams(searchParams)
      next.set('profit', desired)
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profitBasis, location.pathname, activeOrgId])

  // Fetch countries and variants when an active organization is selected; clear when none
  useEffect(() => {
    const fetchCountries = async (): Promise<void> => {
      try {
        setIsLoadingCountries(true)
        const countriesData = await countryService.getCountries()
        const list: SupportedCountryDTO[] = (Array.isArray(countriesData) ? countriesData : []).map(
          c => ({ code: c.code, name: c.name })
        )
        setAvailableCountries(list)
      } catch (error: unknown) {
        console.error('Error fetching countries:', error)
        setAvailableCountries([])
      } finally {
        setIsLoadingCountries(false)
      }
    }

    const fetchVariants = async (): Promise<void> => {
      try {
        setIsLoadingVariants(true)
        const variantsData = await getAllVariants()
        if (!Array.isArray(variantsData)) {
          console.warn(
            '[FilterProvider] getAllVariants returned non-array, normalizing to []',
            variantsData
          )
          setAvailableVariants([])
        } else {
          setAvailableVariants(variantsData)
        }
      } catch (error: unknown) {
        console.error('Error fetching variants:', error)
        setAvailableVariants([])
      } finally {
        setIsLoadingVariants(false)
      }
    }

    const run = async (): Promise<void> => {
      if (activeOrgId) {
        await fetchCountries()
        await fetchVariants()
      } else {
        setAvailableCountries([])
        setIsLoadingCountries(false)
        setAvailableVariants([])
        setIsLoadingVariants(false)
      }
    }

    void run()
  }, [activeOrgId])

  /**
   * Function to set date range for both start and end dates.
   *
   * @param start - Start date or null to clear
   * @param end - End date or null to clear
   */
  const setDateRange = (start: Date | null, end: Date | null): void => {
    console.debug('[FilterProvider] setDateRange called', { start, end })
    setStartDate(start)
    setEndDate(end)

    if (start && end) {
      const next = inferTimeAggregation(start, end, aggregation)
      console.debug('[FilterProvider] next aggregation:', next)
      if (next !== aggregation) {
        console.debug('[FilterProvider] updating aggregation from', aggregation, 'to', next)
        setAggregation(next)
      }
    }
  }

  // Create the context value object
  const value: FilterContextType = {
    startDate,
    endDate,
    setDateRange,
    aggregation,
    setAggregation,
    countries,
    setCountries,
    availableCountries,
    isLoadingCountries,
    skus,
    setSkus,
    availableSkus: availableVariants,
    isLoadingSkus: isLoadingVariants,
    tags,
    setTags,
    availableTags,
    isLoadingTags,
    profitBasis,
    setProfitBasis: (basis: 'OC' | 'CM2') => setProfitBasis(basis),
  }

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
}
